import React, { useEffect, useState } from "react";
import axios from "axios";
import { ArrowLeft, Warning } from "phosphor-react";
import { Form, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import AsyncSelect from "react-select/async";
import Loader from "../CustomCommons/Loader";
import { showToast } from "../../utils/ToastHelper";
import { BASE_URL } from "../Const/Url";
import {
  GC_API,
  GC_KIND,
  StatusPill,
  dateTime,
  isSuperAdmin,
  money,
  searchInvoices,
  showApiError,
} from "./giftCardCommon";
import "./css/giftcard.css";

const Fact = ({ label, children }) => (
  <div className="gc-fact">
    <p className="gc-fact__label">{label}</p>
    <p className="gc-fact__value">{children ?? "—"}</p>
  </div>
);

const GiftCardDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const canDelete = isSuperAdmin();
  const [card, setCard] = useState(null);
  const [ledger, setLedger] = useState({ transactions: [], pin_attempts: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showActivate, setShowActivate] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [showResetPin, setShowResetPin] = useState(false);
  const [showDeleteCard, setShowDeleteCard] = useState(false);
  const [newPinResult, setNewPinResult] = useState(null);
  const [activateForm, setActivateForm] = useState({
    expiry_date: "",
    invoice: "",
    issued_to_name: "",
    issued_to_email: "",
    issued_to_mobile: "",
  });
  const [disableReason, setDisableReason] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [manualPin, setManualPin] = useState("");

  // The list row is a summary, so read the invoice back for the contact fields.
  const pickInvoice = (option) => {
    setSelectedInvoice(option);
    if (!option) {
      setActivateForm((form) => ({ ...form, invoice: "" }));
      return;
    }
    axios
      .get(`${BASE_URL}api/v1/sales/inventory/invoice/${option.value}/`)
      .then((res) => {
        const inv = res.data?.data || {};
        setActivateForm((form) => ({
          ...form,
          invoice: inv.id ?? option.invoice?.id ?? "",
          issued_to_name: inv.bill_to || form.issued_to_name,
          issued_to_mobile: inv.to_mobile || form.issued_to_mobile,
          issued_to_email: inv.to_email || form.issued_to_email,
        }));
      })
      .catch(() => showToast("error", "Could not read that invoice"));
  };

  const loadCard = () =>
    axios
      .get(`${GC_API}admin/card/${id}/`)
      .then((res) => setCard(res.data?.data))
      .catch((err) => showApiError(err, "Could not load the card"));

  const loadHistory = () =>
    axios
      .get(`${GC_API}admin/card/${id}/history/`)
      .then((res) =>
        setLedger({
          transactions: res.data?.data?.transactions || [],
          pin_attempts: res.data?.data?.pin_attempts || [],
        })
      )
      .catch(() => {});

  useEffect(() => {
    Promise.all([loadCard(), loadHistory()]).finally(() => setIsLoading(false));
  }, [id]);

  const refresh = () => Promise.all([loadCard(), loadHistory()]);

  const post = (path, payload, successMsg, onSuccess) => {
    setBusy(true);
    axios
      .post(`${GC_API}admin/card/${id}/${path}/`, payload)
      .then((res) => {
        showToast("success", successMsg);
        if (onSuccess) onSuccess(res.data?.data);
        refresh();
      })
      .catch((err) => showApiError(err))
      .finally(() => setBusy(false));
  };

  const activate = () => {
    // Every field is optional - leave expiry out for a card that never expires.
    const payload = {};
    Object.entries(activateForm).forEach(([key, value]) => {
      if (value !== "") payload[key] = key === "invoice" ? Number(value) : value;
    });
    post("activate", payload, "Card activated", () => setShowActivate(false));
  };

  const disable = () =>
    post(
      "disable",
      disableReason ? { reason: disableReason } : {},
      "Card disabled",
      () => {
        setShowDisable(false);
        setDisableReason("");
      }
    );

  const resetPin = () => {
    if (manualPin && !/^\d{6}$/.test(manualPin)) {
      showToast("error", "PIN must be exactly 6 digits");
      return;
    }
    post(
      "reset-pin",
      manualPin ? { new_pin: manualPin } : {},
      "PIN reset",
      (data) => {
        setShowResetPin(false);
        setManualPin("");
        setNewPinResult({ pin: data?.pin, detail: data?.detail });
      }
    );
  };

  // Deleted cards vanish from every list, so there's nothing left here to
  // refresh - leave the page instead of calling refresh() like the other
  // actions do.
  const deleteCard = () => {
    setBusy(true);
    axios
      .post(`${GC_API}admin/card/${id}/soft-delete/`)
      .then((res) => {
        const data = res.data?.data ?? res.data ?? {};
        showToast("success", data.detail || "Card deleted");
        history.push("/gift-card/card-list");
      })
      .catch((err) => {
        // Already gone (bad id or already hidden) - nothing left to show either.
        if (err?.request?.status === 404) {
          showToast("success", "This card is already gone");
          history.push("/gift-card/card-list");
          return;
        }
        showApiError(err);
      })
      .finally(() => setBusy(false));
  };

  if (isLoading) {
    return (
      <div className="categorey-parent gc-page">
        <div className="gc-loading">
          <Loader />
        </div>
      </div>
    );
  }
  if (!card) return null;

  const oneAttemptLeft = Number(card.failure_limit) === 1;

  return (
    <div className="categorey-parent gc-page">
      <div className="gc-head">
        <div>
          <h3>Card Detail</h3>
          <p className="gc-sub">
            Everything customer care needs for a single gift card.
          </p>
        </div>
        <div className="gc-actions">
          <button
            className="gc-btn gc-btn--ghost"
            onClick={() => history.goBack()}
          >
            <ArrowLeft size={16} weight="bold" />
            Back
          </button>
        </div>
      </div>

      <div className="gc-panel">
        <div className="gc-detail__head">
          <div>
            <h4 className="gc-detail__number">
              {card.formatted_number || card.number}
            </h4>
            <StatusPill status={card.status} label={card.status_display} />
          </div>
          <div className="gc-actions">
            {card.status === 0 && (
              <button className="gc-btn" onClick={() => setShowActivate(true)}>
                Activate
              </button>
            )}
            {card.status === 1 && (
              <button
                className="gc-btn gc-btn--danger"
                onClick={() => setShowDisable(true)}
              >
                Disable
              </button>
            )}
            {card.status === 3 && (
              <button className="gc-btn" onClick={() => setShowResetPin(true)}>
                Reset PIN
              </button>
            )}
            {(card.status === 0 || card.status === 3) && (
              <button
                className="gc-btn gc-btn--danger"
                disabled={!canDelete}
                title={canDelete ? undefined : "Only Super Admin can delete a card"}
                onClick={() => setShowDeleteCard(true)}
              >
                Delete
              </button>
            )}
          </div>
        </div>

        <div className="gc-body">
          {oneAttemptLeft && (
            <div className="gc-banner">
              <Warning size={20} weight="fill" />
              <span>
                This card has already had its PIN reset once. The <b>next</b>{" "}
                incorrect attempt locks it again — make sure the customer knows
                they get a single try.
              </span>
            </div>
          )}

          <p className="gc-section-title">Card</p>
          <div className="gc-cardface">
            <div className="gc-cardface__top">
              <div>
                <p className="gc-cardface__brand">Kaaruj Gift Card</p>
                <p className="gc-cardface__number">
                  {card.formatted_number || card.number}
                </p>
              </div>
              {card.barcode_url && (
                <img
                  className="gc-cardface__barcode"
                  src={card.barcode_url}
                  alt={`Barcode for card ${card.number}`}
                />
              )}
            </div>

            <ul className="gc-cardface__terms">
              <li>
                This gift card is for a single transaction. It is spent in full
                and no change is given on any remaining value.
              </li>
              <li>
                The order must total at least the value of this card.
              </li>
              <li>
                Any cost above the value of this card is paid by the bearer.
              </li>
              <li>
                Not exchangeable for cash, and not replaced if lost or stolen.
              </li>
              <li>
                Present the card at a Kaaruj outlet, or enter the number and PIN
                at checkout on kaaruj.com.
              </li>
              <li>
                Three incorrect PIN entries lock the card until customer care
                resets it.
              </li>
            </ul>

            <div className="gc-cardface__boxes">
              <div className="gc-cardface__box">
                <span className="gc-cardface__box-label">PIN</span>
                <span className="gc-code gc-cardface__box-value">
                  {card.pin || "—"}
                </span>
              </div>
              <div className="gc-cardface__box">
                <span className="gc-cardface__box-label">Expiration Date</span>
                <span className="gc-cardface__box-value">
                  {card.expiry_date || "No expiry"}
                </span>
              </div>
            </div>
          </div>

          <div className="gc-facts">
            <Fact label="SKU">
              <span className="gc-code">{card.sku}</span>
            </Fact>
            <Fact label="Value">{money(card.initial_amount)}</Fact>
            <Fact label="Balance">{money(card.balance)}</Fact>
            <Fact label="Failed PIN Attempts">
              <span style={oneAttemptLeft ? { color: "#a3302a" } : undefined}>
                {card.failed_attempts} of {card.failure_limit}
              </span>
            </Fact>
            <Fact label="Activated At">{dateTime(card.activated_at)}</Fact>
          </div>

          <p className="gc-section-title">Issued To</p>
          <div className="gc-facts">
            <Fact label="Name">{card.issued_to_name}</Fact>
            <Fact label="Mobile">{card.issued_to_mobile}</Fact>
            <Fact label="Email">{card.issued_to_email}</Fact>
            {card.disabled_at && (
              <>
                <Fact label="Disabled At">{dateTime(card.disabled_at)}</Fact>
                <Fact label="Disabled Reason">{card.disabled_reason}</Fact>
              </>
            )}
          </div>

          <p className="gc-section-title">Transactions</p>
          <div className="gc-subtable gc-scroll">
            <table className="gc-table">
              <thead>
                <tr>
                  <th style={{ width: "130px" }}>Kind</th>
                  <th style={{ width: "130px" }}>Source</th>
                  <th style={{ width: "130px" }}>Amount</th>
                  <th style={{ width: "150px" }}>Balance After</th>
                  <th style={{ width: "220px" }}>Note</th>
                  <th style={{ width: "180px" }}>When</th>
                </tr>
              </thead>
              <tbody>
                {ledger.transactions.length > 0 ? (
                  ledger.transactions.map((tx, i) => (
                    <tr className="gc-row" key={i}>
                      <td className="gc-strong">
                        {tx?.kind_display || GC_KIND[tx?.kind] || "—"}
                      </td>
                      <td>{tx?.source_display || "—"}</td>
                      <td className="gc-num">{money(tx?.amount)}</td>
                      <td className="gc-num">{money(tx?.balance_after)}</td>
                      <td>{tx?.note || "—"}</td>
                      <td className="gc-num">{dateTime(tx?.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6">
                      <div className="gc-empty">
                        <h4>No transactions</h4>
                        <p>Nothing has moved on this card yet.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="gc-section-title">PIN Attempts</p>
          <div className="gc-subtable gc-scroll">
            <table className="gc-table" style={{ minWidth: "560px" }}>
              <thead>
                <tr>
                  <th style={{ width: "140px" }}>Result</th>
                  <th style={{ width: "200px" }}>IP Address</th>
                  <th style={{ width: "200px" }}>When</th>
                </tr>
              </thead>
              <tbody>
                {ledger.pin_attempts.length > 0 ? (
                  ledger.pin_attempts.map((attempt, i) => (
                    <tr className="gc-row" key={i}>
                      <td>
                        <span
                          className={`gc-pill gc-pill--${
                            attempt?.succeeded ? "ok" : "crit"
                          }`}
                        >
                          {attempt?.succeeded ? "Success" : "Failed"}
                        </span>
                      </td>
                      <td className="gc-code">{attempt?.ip_address || "—"}</td>
                      <td className="gc-num">{dateTime(attempt?.created_at)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="3">
                      <div className="gc-empty">
                        <h4>No PIN attempts</h4>
                        <p>Nobody has entered a PIN for this card.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Modal
        centered
        dialogClassName="gc-modal"
        show={showActivate}
        onHide={() => !busy && setShowActivate(false)}
      >
        <Modal.Header>
          <Modal.Title>Activate Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="gc-hint" style={{ margin: "0 0 16px" }}>
            Every field is optional. Leave the expiry blank for a card that never
            expires.
          </p>
          <div className="gc-form-grid">
            <div>
              <label className="gc-label" htmlFor="gc-expiry">
                Expiry Date
              </label>
              <Form.Control
                id="gc-expiry"
                className="gc-input"
                type="date"
                value={activateForm.expiry_date}
                onChange={(e) =>
                  setActivateForm({
                    ...activateForm,
                    expiry_date: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-invoice">
                Invoice
              </label>
              <AsyncSelect
                inputId="gc-invoice"
                isClearable
                cacheOptions
                value={selectedInvoice}
                onChange={pickInvoice}
                loadOptions={searchInvoices}
                placeholder="Search invoice number or customer"
                maxMenuHeight={220}
                // the modal clips its own overflow, so the list goes to the body
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
                }}
                noOptionsMessage={({ inputValue }) =>
                  inputValue.trim().length < 2
                    ? "Type at least 2 characters"
                    : "No invoice found"
                }
              />
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-name">
                Issued To Name
              </label>
              <Form.Control
                id="gc-name"
                className="gc-input"
                value={activateForm.issued_to_name}
                onChange={(e) =>
                  setActivateForm({
                    ...activateForm,
                    issued_to_name: e.target.value,
                  })
                }
              />
            </div>
            <div>
              <label className="gc-label" htmlFor="gc-mobile">
                Issued To Mobile
              </label>
              <Form.Control
                id="gc-mobile"
                className="gc-input"
                placeholder="+8801700000000"
                value={activateForm.issued_to_mobile}
                onChange={(e) =>
                  setActivateForm({
                    ...activateForm,
                    issued_to_mobile: e.target.value,
                  })
                }
              />
            </div>
            <div className="gc-form-grid--full">
              <label className="gc-label" htmlFor="gc-email">
                Issued To Email
              </label>
              <Form.Control
                id="gc-email"
                className="gc-input"
                type="email"
                value={activateForm.issued_to_email}
                onChange={(e) =>
                  setActivateForm({
                    ...activateForm,
                    issued_to_email: e.target.value,
                  })
                }
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={busy}
            onClick={() => setShowActivate(false)}
          >
            Cancel
          </button>
          <button className="gc-btn" disabled={busy} onClick={activate}>
            Activate
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        dialogClassName="gc-modal"
        show={showDisable}
        onHide={() => !busy && setShowDisable(false)}
      >
        <Modal.Header>
          <Modal.Title>Disable Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <label className="gc-label" htmlFor="gc-reason">
            Reason
          </label>
          <Form.Control
            id="gc-reason"
            className="gc-input"
            placeholder="Reported stolen"
            value={disableReason}
            onChange={(e) => setDisableReason(e.target.value)}
          />
          <p className="gc-hint">
            Optional, but it is what staff see later when the customer asks why.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={busy}
            onClick={() => setShowDisable(false)}
          >
            Cancel
          </button>
          <button
            className="gc-btn gc-btn--danger"
            disabled={busy}
            onClick={disable}
          >
            Disable
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        dialogClassName="gc-modal"
        show={showDeleteCard}
        onHide={() => !busy && setShowDeleteCard(false)}
      >
        <Modal.Header>
          <Modal.Title>Delete Card</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="gc-note">
            Are you sure you want to delete card{" "}
            {card.formatted_number || card.number}?
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={busy}
            onClick={() => setShowDeleteCard(false)}
          >
            Cancel
          </button>
          <button
            className="gc-btn gc-btn--danger"
            disabled={busy}
            onClick={deleteCard}
          >
            Delete
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        centered
        dialogClassName="gc-modal"
        show={showResetPin}
        onHide={() => !busy && setShowResetPin(false)}
      >
        <Modal.Header>
          <Modal.Title>Reset PIN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="gc-banner">
            <Warning size={20} weight="fill" />
            <span>
              After the reset this card locks after <b>one</b> incorrect attempt.
            </span>
          </div>
          <label className="gc-label" htmlFor="gc-newpin">
            New PIN
          </label>
          <Form.Control
            id="gc-newpin"
            className="gc-input"
            placeholder="6 digits"
            maxLength={6}
            inputMode="numeric"
            value={manualPin}
            onChange={(e) => setManualPin(e.target.value)}
          />
          <p className="gc-hint">Leave blank to generate one automatically.</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="gc-btn gc-btn--ghost"
            disabled={busy}
            onClick={() => setShowResetPin(false)}
          >
            Cancel
          </button>
          <button className="gc-btn" disabled={busy} onClick={resetPin}>
            Reset PIN
          </button>
        </Modal.Footer>
      </Modal>

      {/* The new PIN comes back only in the reset response - show it before the dialog closes. */}
      <Modal
        centered
        dialogClassName="gc-modal"
        show={!!newPinResult}
        onHide={() => setNewPinResult(null)}
      >
        <Modal.Header>
          <Modal.Title>New PIN</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="gc-pin">{newPinResult?.pin}</p>
          <p className="gc-hint" style={{ textAlign: "center" }}>
            {newPinResult?.detail}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <button className="gc-btn" onClick={() => setNewPinResult(null)}>
            I have noted it down
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GiftCardDetail;
